/**
 * Maps submission failure modes to user-friendly error messages.
 *
 * Raw error detail stays in console output for debugging. The user only sees the mapped message.
 */

import { WorkflowStage } from "@/generated/sdk"

interface SubmissionError {
  userMessage: string
  stage: "presign" | "upload" | "unknown"
}

/**
 * Explains why a DTA's workflow stage does not accept data submissions, or null when the stage
 * itself is no obstacle (ACTIVE).
 *
 * Mirrors the stage gating in
 * `packages/backend/lib/authorization_layer/cedar-policies/capability-policies.cedar`: a Live Run
 * upload requires ACTIVE, and a Test Run is refused on an INACTIVE stream.
 */
export function describeStageSubmissionBlock(dtaStage: string): string | null {
  switch (dtaStage) {
    case WorkflowStage.Draft:
      return "This DTA is still in draft and not ready to receive data."
    case WorkflowStage.Review:
      return "This DTA is still under review. Data submissions open once it becomes active."
    case WorkflowStage.Approved:
      return "This DTA is approved but not yet active. Data submissions open once it becomes active."
    case WorkflowStage.Inactive:
      return "This DTA is inactive and no longer accepts data submissions."
    default:
      return null
  }
}

function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true
  if (error instanceof Error) {
    if (error.name === "FetchError") return true
    if ("cause" in error && error.cause instanceof TypeError) return true
    const lower = error.message.toLowerCase()
    return (
      lower === "failed to fetch" ||
      lower === "load failed" ||
      lower === "network failure" ||
      lower.includes("networkerror") ||
      lower.includes("err_network") ||
      lower.includes("err_internet_disconnected")
    )
  }
  return false
}

function extractHttpStatus(error: unknown): number | null {
  if (error instanceof Error && "response" in error && error.response instanceof Response) {
    return error.response.status
  }
  const message = error instanceof Error ? error.message : String(error)
  const statusMatch = message.match(/(?:status|API Error:)\s*(\d{3})/)
  if (statusMatch) return Number.parseInt(statusMatch[1], 10)
  return null
}

/**
 * @param dtaStage Workflow stage the DTA was in when the request was made, when the caller knows
 *   it. A 403 is usually the stage gate rather than a true permissions problem, so passing this
 *   turns an ambiguous "you do not have permission" into a lifecycle explanation.
 */
export function mapPresignError(error: unknown, dtaStage?: string): SubmissionError {
  if (isNetworkError(error)) {
    return {
      userMessage: "Unable to reach the server. Check your internet connection and try again.",
      stage: "presign",
    }
  }

  const status = extractHttpStatus(error)
  if (status) {
    switch (status) {
      case 401:
        return {
          userMessage: "Your session has expired. Please refresh the page and sign in again.",
          stage: "presign",
        }
      case 403: {
        // A 403 at a non-ACTIVE stage is the lifecycle gate, not a permissions problem. This is
        // reachable when the stage changes while the page is open. At ACTIVE the denial genuinely
        // is about permissions, so keep the original wording.
        const stageBlock = dtaStage ? describeStageSubmissionBlock(dtaStage) : null
        return {
          userMessage: stageBlock
            ? `${stageBlock} Contact your study administrator if you believe this is an error.`
            : "You do not have permission to upload to this DTA. Contact your study administrator if you believe this is an error.",
          stage: "presign",
        }
      }
      case 400:
        return {
          userMessage:
            "The upload request was rejected. Verify the file name and DTA configuration, then try again.",
          stage: "presign",
        }
      case 404:
        return {
          userMessage:
            "This DTA could not be found. It may have been removed or you may not have access.",
          stage: "presign",
        }
      case 429:
        return {
          userMessage: "Too many requests. Please wait a moment and try again.",
          stage: "presign",
        }
      default:
        if (status >= 500) {
          return {
            userMessage:
              "The server encountered an error while preparing your upload. Please try again in a few minutes.",
            stage: "presign",
          }
        }
    }
  }

  return {
    userMessage: "Failed to prepare file upload. Please try again.",
    stage: "presign",
  }
}

export function mapUploadError(error: unknown): SubmissionError {
  if (isNetworkError(error)) {
    return {
      userMessage: "The file upload was interrupted. Check your internet connection and try again.",
      stage: "upload",
    }
  }

  const status = extractHttpStatus(error)
  if (status) {
    switch (status) {
      case 403:
        return {
          userMessage:
            "The upload was rejected by storage. The upload link may have expired — please try again.",
          stage: "upload",
        }
      case 400:
        return {
          userMessage:
            "The file was rejected by storage. Verify the file is not corrupted and try again.",
          stage: "upload",
        }
      case 413:
        return {
          userMessage:
            "The file is too large to upload. Please reduce the file size and try again.",
          stage: "upload",
        }
      default:
        if (status >= 500) {
          return {
            userMessage:
              "The storage service encountered an error. Please try again in a few minutes.",
            stage: "upload",
          }
        }
    }
  }

  return {
    userMessage: "File upload failed. Please try again.",
    stage: "upload",
  }
}
